import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../shared/api/client';
import { Card } from '../shared/ui/Card';
import { ParkingDataTable } from '../shared/ui/ParkingDataTable';
import { AppModal } from '../shared/ui/AppModal';
import { StatusBadge, statusToneFromEnum } from '../shared/ui/StatusBadge';

export function PlatformSettingsPage() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [supportEmail, setSupportEmail] = useState('');
  const [vapidSubject, setVapidSubject] = useState('');
  const [adminOpen, setAdminOpen] = useState(false);
  const [adminForm, setAdminForm] = useState({
    email: '',
    password: '',
    firstName: '',
    lastName: '',
  });
  const [error, setError] = useState<string | null>(null);
  const [ok, setOk] = useState<string | null>(null);

  const settings = useQuery({
    queryKey: ['platform-settings'],
    queryFn: () => api.platformGetSettings(),
  });
  const admins = useQuery({
    queryKey: ['platform-admins'],
    queryFn: () => api.platformListAdmins(),
  });

  useEffect(() => {
    if (settings.data?.data) {
      setSupportEmail(settings.data.data.supportEmail ?? '');
      setVapidSubject(settings.data.data.vapidSubject);
    }
  }, [settings.data]);

  const save = useMutation({
    mutationFn: () =>
      api.platformUpdateSettings({
        supportEmail: supportEmail || null,
        vapidSubject: vapidSubject || undefined,
      }),
    onSuccess: async () => {
      setOk(t('platform.settings.saved'));
      setError(null);
      await qc.invalidateQueries({ queryKey: ['platform-settings'] });
    },
    onError: (e: Error) => setError(e.message),
  });

  const regen = useMutation({
    mutationFn: () =>
      api.platformUpdateSettings({
        regenerateVapid: true,
        vapidSubject: vapidSubject || undefined,
      }),
    onSuccess: async () => {
      setOk(t('platform.settings.vapidRegenerated'));
      setError(null);
      await qc.invalidateQueries({ queryKey: ['platform-settings'] });
    },
    onError: (e: Error) => setError(e.message),
  });

  const createAdmin = useMutation({
    mutationFn: () => api.platformCreateAdmin(adminForm),
    onSuccess: async () => {
      setAdminOpen(false);
      setAdminForm({ email: '', password: '', firstName: '', lastName: '' });
      await qc.invalidateQueries({ queryKey: ['platform-admins'] });
    },
    onError: (e: Error) => setError(e.message),
  });

  const adminRows = admins.data?.data ?? [];

  return (
    <>
{error && <div className="alert alert-danger">{error}</div>}
      {ok && <div className="alert alert-success">{ok}</div>}

      <Card title={t('platform.settings.push')} className="mb-4">
        <div className="d-grid gap-3" style={{ maxWidth: 560 }}>
          <div>
            <label className="form-label">{t('platform.settings.supportEmail')}</label>
            <input
              className="form-control"
              value={supportEmail}
              onChange={(e) => setSupportEmail(e.target.value)}
            />
          </div>
          <div>
            <label className="form-label">{t('platform.settings.vapidSubject')}</label>
            <input
              className="form-control"
              value={vapidSubject}
              onChange={(e) => setVapidSubject(e.target.value)}
            />
            <div className="form-text">{t('platform.settings.vapidSubjectHint')}</div>
          </div>
          <div>
            <label className="form-label">{t('platform.settings.vapidPublic')}</label>
            <textarea
              className="form-control"
              rows={3}
              readOnly
              value={settings.data?.data.vapidPublicKey ?? ''}
            />
          </div>
          <div className="d-flex flex-wrap gap-2">
            <button
              type="button"
              className="btn btn-primary"
              disabled={save.isPending}
              onClick={() => save.mutate()}
            >
              {t('common.save')}
            </button>
            <button
              type="button"
              className="btn btn-light"
              disabled={regen.isPending}
              onClick={() => {
                if (window.confirm(t('platform.settings.confirmRegen'))) regen.mutate();
              }}
            >
              {t('platform.settings.regenVapid')}
            </button>
          </div>
        </div>
      </Card>

      <Card
        title={t('platform.settings.admins')}
        toolbar={
          <button type="button" className="btn btn-light btn-sm" onClick={() => setAdminOpen(true)}>
            {t('platform.settings.createAdmin')}
          </button>
        }
      >
        <ParkingDataTable
          storageKey="platform-admins"
          columns={[
            t('platform.login.email'),
            t('platform.settings.adminName'),
            t('platform.tenants.status'),
          ]}
          refreshKey={adminRows.map((a) => a.id).join('|')}
          actionsColumn={false}
          galleryItems={adminRows.map((a) => ({
            id: a.id,
            icon: 'bi-shield-lock',
            iconTone: statusToneFromEnum(a.status),
            title: a.email,
            subtitle: `${a.firstName} ${a.lastName}`,
            badge: <StatusBadge tone={statusToneFromEnum(a.status)}>{a.status}</StatusBadge>,
          }))}
        >
          {adminRows.map((a) => (
            <tr key={a.id}>
              <td className="fw-semibold">{a.email}</td>
              <td>
                {a.firstName} {a.lastName}
              </td>
              <td>
                <StatusBadge tone={statusToneFromEnum(a.status)}>{a.status}</StatusBadge>
              </td>
            </tr>
          ))}
        </ParkingDataTable>
      </Card>

      <AppModal
        open={adminOpen}
        onClose={() => setAdminOpen(false)}
        title={t('platform.settings.createAdmin')}
        footer={
          <>
            <button type="button" className="btn btn-light" onClick={() => setAdminOpen(false)}>
              {t('common.cancel')}
            </button>
            <button
              type="button"
              className="btn btn-primary"
              disabled={createAdmin.isPending}
              onClick={() => createAdmin.mutate()}
            >
              {t('common.save')}
            </button>
          </>
        }
      >
        <div className="d-grid gap-3">
          <div>
            <label className="form-label">{t('platform.login.email')}</label>
            <input
              className="form-control"
              value={adminForm.email}
              onChange={(e) => setAdminForm((f) => ({ ...f, email: e.target.value }))}
            />
          </div>
          <div>
            <label className="form-label">{t('platform.login.password')}</label>
            <input
              className="form-control"
              type="password"
              value={adminForm.password}
              onChange={(e) => setAdminForm((f) => ({ ...f, password: e.target.value }))}
            />
          </div>
          <div>
            <label className="form-label">{t('platform.tenants.adminFirstName')}</label>
            <input
              className="form-control"
              value={adminForm.firstName}
              onChange={(e) => setAdminForm((f) => ({ ...f, firstName: e.target.value }))}
            />
          </div>
          <div>
            <label className="form-label">{t('platform.tenants.adminLastName')}</label>
            <input
              className="form-control"
              value={adminForm.lastName}
              onChange={(e) => setAdminForm((f) => ({ ...f, lastName: e.target.value }))}
            />
          </div>
        </div>
      </AppModal>
    </>
  );
}
